
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

// Token helper
const getToken = () => localStorage.getItem("idToken");

// ----------- ASYNC THUNKS -----------

export const fetchWeeklyVisitors = createAsyncThunk(
	"dashboard/fetchWeeklyVisitors",
	async () => {
		const response = await fetch("/api/dashboard/weekly-visitors", {
			headers: { Authorization: `Bearer ${getToken()}` },
		});
		if (!response.ok) {
			throw new Error("Failed to fetch weekly stats");
		}
		return response.json();
	}
);

export const fetchNewWeeklyMembers = createAsyncThunk(
	"dashboard/fetchNewWeeklyMembers",
	async () => {
		const response = await fetch("/api/dashboard/new-weekly-members", {
			headers: { Authorization: `Bearer ${getToken()}` },
		});
		if (!response.ok) {
			throw new Error("Failed to fetch new weekly members");
		}
		return response.json();
	}
);

export const fetchMonthlyVisitors = createAsyncThunk(
	"dashboard/fetchMonthlyVisitors",
	async () => {
		const response = await fetch("/api/dashboard/monthly-visitors", {
			headers: { Authorization: `Bearer ${getToken()}` },
		});
		if (!response.ok) {
			throw new Error("Failed to fetch monthly visitors");
		}
		return response.json();
	}
);

export const fetchTotalMembers = createAsyncThunk(
	"dashboard/fetchTotalMembers",
	async () => {
		const response = await fetch("/api/dashboard/total-members", {
			headers: { Authorization: `Bearer ${getToken()}` },
		});
		if (!response.ok) {
			throw new Error("Failed to fetch total members");
		}
		return response.json();
	}
);

export const fetchMonthlyVisitsGraph = createAsyncThunk(
	"dashboard/fetchMonthlyVisitsGraph",
	async () => {
		const response = await fetch("/api/dashboard/monthly-visits-graph", {
			headers: { Authorization: `Bearer ${getToken()}` },
		});
		if (!response.ok) {
			throw new Error("Failed to fetch monthly visits graph");
		}
		return response.json();
	}
);

export const fetchDietaryStats = createAsyncThunk(
	"dashboard/fetchDietaryStats",
	async () => {
		const response = await fetch("/api/dashboard/dietary-distribution", {
			headers: { Authorization: `Bearer ${getToken()}` },
		});
		if (!response.ok) {
			throw new Error("Failed to fetch dietary stats");
		}
		return response.json();
	}
);

// ----------- INITIAL STATE -----------

const initialState = {
	weeklyVisitors: null,
	newWeeklyMembers: null,
	monthlyVisitors: null,
	totalMembers: null,
	monthlyVisitsGraph: null,
	dietaryStats: null,
	status: "idle",
	error: null,
};

// ----------- SLICE -----------

const dashboardSlice = createSlice({
	name: "dashboard",
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(fetchWeeklyVisitors.fulfilled, (state, action) => {
				state.weeklyVisitors = action.payload;
			})
			.addCase(fetchNewWeeklyMembers.fulfilled, (state, action) => {
				state.newWeeklyMembers = action.payload;
			})
			.addCase(fetchMonthlyVisitors.fulfilled, (state, action) => {
				state.monthlyVisitors = action.payload;
			})
			.addCase(fetchTotalMembers.fulfilled, (state, action) => {
				state.totalMembers = action.payload;
			})
			.addCase(fetchMonthlyVisitsGraph.fulfilled, (state, action) => {
				state.monthlyVisitsGraph = action.payload;
			})
			.addCase(fetchDietaryStats.fulfilled, (state, action) => {
				state.dietaryStats = action.payload;
			})

		// Optional: set global status
			.addMatcher(
				(action) => action.type.startsWith("dashboard/") && action.type.endsWith("/pending"),
				(state) => {
					state.status = "loading";
				}
			)
			.addMatcher(
				(action) => action.type.startsWith("dashboard/") && action.type.endsWith("/fulfilled"),
				(state) => {
					state.status = "succeeded";
					state.error = null;
				}
			)
			.addMatcher(
				(action) => action.type.startsWith("dashboard/") && action.type.endsWith("/rejected"),
				(state, action) => {
					state.status = "failed";
					state.error = action.error.message;
				}
			);
	},
});

export default dashboardSlice.reducer;
export const dashboardReducer = dashboardSlice.reducer;
